import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { filter, map, take } from 'rxjs';

import { logoForTheme, TENANT_CONFIGURATION_HANDLER } from './core/tenant';
import { PORTAL_AVAILABILITY } from './core/tokens/app.tokens';
import { AUTH_HANDLER } from './features/auth';
import { LOCALIZATION_HANDLER, THEME_HANDLER } from './features/shell';
import { SplashScreenComponent } from './shared/components/splash-screen/splash-screen.component';
import {
  ServiceUnavailableComponent,
  type ServiceUnavailableLabels,
} from './shared/components/service-unavailable/service-unavailable.component';

/**
 * La radice mostra l'indisponibilita' del portale, e non una delle due cornici: puo' toccare prima che
 * una cornice sia montata — una guardia la cui sonda non ha avuto risposta non apre niente — e vale per
 * entrambe allo stesso modo.
 */
@Component({
  selector: 'dxp-root',
  standalone: true,
  imports: [RouterOutlet, ServiceUnavailableComponent, SplashScreenComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly availability = inject(PORTAL_AVAILABILITY);
  private readonly localizationHandler = inject(LOCALIZATION_HANDLER);
  private readonly authHandler = inject(AUTH_HANDLER);
  private readonly themeHandler = inject(THEME_HANDLER);
  private readonly tenantConfiguration = inject(TENANT_CONFIGURATION_HANDLER);

  /**
   * La schermata che copre il portale, se ce n'e' una: il servizio che non risponde, da cui si riprova, o
   * l'operatore non abilitato sull'ente da cui accede, da cui si esce — l'abilitazione arriva col prossimo
   * accesso, non riprovando.
   */
  protected readonly blockingScreen = computed<BlockingScreen | null>(() => {
    switch (this.availability.status()) {
      case 'unavailable':
        return { labels: this.unavailableLabels(), act: () => this.availability.retry() };
      case 'operator-not-enabled':
        return { labels: this.operatorNotEnabledLabels(), act: () => this.signOut() };
      default:
        return null;
    }
  });

  /**
   * Se la prima pagina e' pronta. Fino ad allora le guardie stanno ancora chiedendo chi e' che arriva e
   * per quale ente — qualche secondo — e sotto non c'e' niente da vedere: resta la schermata d'apertura,
   * la stessa che `index.html` ha disegnato prima che l'applicazione partisse. Una navigazione annullata
   * o fallita chiude l'attesa lo stesso: tenere lo schermo fermo sul logo sarebbe peggio.
   */
  protected readonly isOpening = toSignal(
    inject(Router).events.pipe(
      filter(
        (event) =>
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError,
      ),
      take(1),
      map(() => false),
    ),
    { initialValue: true },
  );

  protected readonly splashLabel = computed(() => {
    this.localizationHandler.currentLanguage();
    return this.localizationHandler.translate('shell.opening');
  });

  protected readonly brandName = this.localizationHandler.productName;

  /**
   * Il logo del tenant per il tema dipinto adesso — quello arrivato con la configurazione o, prima,
   * quello ricordato — oppure `null`, e la schermata disegna il logo del portale.
   */
  protected readonly logoUrl = computed(() =>
    logoForTheme(this.tenantConfiguration.logoUrls(), this.themeHandler.isDark()),
  );

  /** Il marchio del tenant per il riquadro chiaro della schermata di guasto, o `null` per le iniziali (#1850). */
  protected readonly markUrl = computed(() => this.tenantConfiguration.markUrls().light);

  private readonly unavailableLabels = computed<ServiceUnavailableLabels>(() =>
    this.screenLabels('unavailable.title', 'unavailable.message', 'unavailable.retry'),
  );

  private readonly operatorNotEnabledLabels = computed<ServiceUnavailableLabels>(() =>
    this.screenLabels(
      'operatorNotEnabled.title',
      'operatorNotEnabled.message',
      'operatorNotEnabled.signOut',
    ),
  );

  private screenLabels(title: string, message: string, action: string): ServiceUnavailableLabels {
    this.localizationHandler.currentLanguage();
    return {
      brandName: this.localizationHandler.productName(),
      title: this.localizationHandler.translate(title),
      message: this.localizationHandler.translate(message),
      action: this.localizationHandler.translate(action),
    };
  }

  /** Come in intestazione: l'uscita e' un redirect fuori origine, quindi lo percorre il browser. */
  private signOut(): void {
    window.location.href = this.authHandler.logoutUrl();
  }
}

interface BlockingScreen {
  readonly labels: ServiceUnavailableLabels;
  readonly act: () => void;
}
